/**
 * Where a saved picture lands on disk.
 *
 * A plain `<a download>` cannot choose a folder — the browser puts the file in Downloads and
 * that is the end of it. The File System Access API can: the person picks a folder once, the
 * studio makes a `CStudio` folder inside it, and every later save goes straight there with no
 * further prompting, because the granted handle is kept in the same IndexedDB the rest of the
 * local layer uses.
 *
 * Where that API is missing (Safari, Firefox, iOS) the download is still the right answer, so
 * this falls back to it rather than refusing to save. Callers do not need to care which
 * happened: saveImageFile() reports back which route it took so the message can be accurate.
 */
import { DOCS, idbGet, idbPut } from './idb';

const HANDLE_KEY = 'fs:cstudio-folder';
export const FOLDER_NAME = 'CS_Studio';

/**
 * Whether this device can be given a folder to save into.
 *
 * Wherever the browser offers it — including a phone whose browser happens to support it. The
 * cost of offering it is one prompt, once, and the grant is then remembered (see below), so the
 * thing that made it feel intrusive was re-asking, not asking.
 */
export const canPickFolder = () => typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

// Asked and refused already in this session: don't ask again until the app is reopened.
let declinedThisSession = false;

const readHandle = async () => {
  try { return (await idbGet(DOCS, HANDLE_KEY))?.data?.handle || null; } catch { return null; }
};
const writeHandle = async (handle) => {
  try { await idbPut(DOCS, { key: HANDLE_KEY, collection: 'fs', id: 'cstudio-folder', data: { handle }, createdAt: new Date().toISOString() }); } catch { /* private mode */ }
};

/** Has permission survived since last time? Asks again only if it has lapsed. */
const stillAllowed = async (handle) => {
  try {
    const opts = { mode: 'readwrite' };
    if ((await handle.queryPermission(opts)) === 'granted') return true;
    return (await handle.requestPermission(opts)) === 'granted';
  } catch { return false; }
};

/**
 * The CStudio folder handle, asking for a parent folder the first time only.
 * Returns null when the API is unavailable or the person dismisses the picker.
 */
export const getStudioFolder = async ({ prompt = true } = {}) => {
  if (!canPickFolder()) return null;
  const saved = await readHandle();

  if (saved) {
    // The folder has already been chosen. Usually the grant is still live and this is silent.
    try {
      if ((await saved.queryPermission({ mode: 'readwrite' })) === 'granted') return saved;
    } catch { /* fall through and ask */ }
    if (declinedThisSession) return null;
    // The browser has let the grant lapse — Chrome keeps it for an INSTALLED app but may drop
    // it for a plain tab. One tap restores it; the picker is never re-opened, because the
    // folder is not in question, only permission to keep writing to it.
    try {
      if ((await saved.requestPermission({ mode: 'readwrite' })) === 'granted') return saved;
    } catch { /* denied or no gesture */ }
    declinedThisSession = true;      // asked once, that is enough
    return null;
  }

  if (!prompt || declinedThisSession) return null;
  try {
    // Downloads, not Pictures: that is where a browser puts things, so it is where somebody
    // looking for what they just saved will look, and a CS_Studio folder made inside it is
    // found without being hunted for.
    const parent = await window.showDirectoryPicker({ id: 'cstudio-save', mode: 'readwrite', startIn: 'downloads' });
    const folder = parent.name === FOLDER_NAME
      ? parent
      : await parent.getDirectoryHandle(FOLDER_NAME, { create: true });
    await writeHandle(folder);
    return folder;
  } catch {
    declinedThisSession = true;      // dismissed: saves go to the downloads for now
    return null;
  }
};

/** Forget the chosen folder, so the next save asks again. */
export const forgetStudioFolder = async () => {
  try { await idbPut(DOCS, { key: HANDLE_KEY, collection: 'fs', id: 'cstudio-folder', data: {}, createdAt: new Date().toISOString() }); } catch { /* ignore */ }
};

/**
 * The name of the folder saves are currently going to, or null if none has been chosen.
 *
 * Read from the handle itself rather than from FOLDER_NAME: once somebody picks a different
 * folder, the constant is no longer the truth, and a label built from the constant would be
 * lying about where their files go.
 */
export const getStudioFolderName = async () => {
  const h = await readHandle();
  if (!h) return null;
  return (await stillAllowed(h)) ? (h.name || null) : null;
};

/**
 * Choose a different folder. Opens the picker straight away and returns the new folder's name
 * (or null if the person backed out, in which case the old choice is left alone).
 */
export const changeStudioFolder = async () => {
  if (!canPickFolder()) return null;
  declinedThisSession = false;       // an explicit choice cancels any earlier refusal
  try {
    const parent = await window.showDirectoryPicker({ id: 'cstudio-save', mode: 'readwrite', startIn: 'downloads' });
    // Picking a folder already called CS_Studio means using it, not nesting another inside it.
    const folder = parent.name === FOLDER_NAME
      ? parent
      : await parent.getDirectoryHandle(FOLDER_NAME, { create: true });
    await writeHandle(folder);
    return folder.name || null;
  } catch {
    return null;
  }
};

/** A name that does not overwrite what is already in the folder: name.png, name (2).png, … */
const freeName = async (folder, base, ext) => {
  for (let n = 1; n < 500; n++) {
    const name = n === 1 ? `${base}.${ext}` : `${base} (${n}).${ext}`;
    try { await folder.getFileHandle(name); } catch { return name; }   // getFileHandle throws = free
  }
  return `${base} ${Date.now()}.${ext}`;
};

/**
 * Write `blob` as `<base>.<ext>`.
 *
 * @returns {Promise<{ where: 'folder'|'download', name: string, folder?: string }>}
 */
export const saveImageFile = async (blob, base, ext, { allowFolder = true } = {}) => {
  const folder = allowFolder ? await getStudioFolder({ prompt: false }) : null;
  if (folder) {
    try {
      const name = await freeName(folder, base, ext);
      const fh = await folder.getFileHandle(name, { create: true });
      const w = await fh.createWritable();
      await w.write(blob);
      await w.close();
      return { where: 'folder', name, folder: folder.name || FOLDER_NAME };
    } catch { /* fall through to the download */ }
  }
  const name = `${base}.${ext}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return { where: 'download', name };
};
